// "Arrive with intent" for pages kept alive by the taskbar.
// Spec: docs/specs/2026-10-07-bo-orders-refresh-on-arrival-design.md §4.1.
//
// Since 2026-10-06 every page with an open taskbar tab is kept alive
// (DashboardLayout.vue, keyed by taskbarKey), so `mounted()` runs once per tab
// and a plain tab switch must NOT refetch. Some arrivals do carry intent,
// though — "I just created an order, show it" — and those must refresh.
//
// The sender calls requestRefresh(path) right before router.push; the page
// calls arrivalIntent(this.$route) in mounted() and in activated() and acts
// only when `refresh` is true. A flag is consumed by the first arrival that
// reads it, so it can never cause a second, stale refresh later.
//
// Keys go through taskbarPathOf — the same identity the keep-alive cache uses
// (path, plus the query for QUERY_KEYED_PATHS) — so a request lands on exactly
// the instance the push lands on.
//
// Consumers: src/views/Backoffice/OrderTables.vue (reader),
// OrderForm.vue / OrderFormQuick.vue (senders). Pinned by
// tests/unit/pageRefresh.spec.js and tests/unit/ordersRefreshOnArrival*.spec.js.
import Vue from "vue";
import { taskbarPathOf } from "./taskbar";

export const refreshRequests = Vue.observable({ keys: {} });

function keyOf(pathOrFullPath) {
	return typeof pathOrFullPath === "string" ? taskbarPathOf(pathOrFullPath) : "";
}

export function requestRefresh(pathOrFullPath) {
	const key = keyOf(pathOrFullPath);
	if (!key) return;
	Vue.set(refreshRequests.keys, key, true);
}

export function hasRefresh(pathOrFullPath) {
	const key = keyOf(pathOrFullPath);
	return !!key && refreshRequests.keys[key] === true;
}

export function consumeRefresh(pathOrFullPath) {
	if (!hasRefresh(pathOrFullPath)) return false;
	Vue.delete(refreshRequests.keys, keyOf(pathOrFullPath));
	return true;
}

// { refresh, detail, boPackingId } for the route the page is arriving on.
// Route params are intent on their own (a push by name with params is never a
// plain tab switch); `0` is a real bo_packing_id, hence `!= null`.
export function arrivalIntent(route) {
	if (!route || typeof route !== "object") return { refresh: false, detail: null, boPackingId: null };
	const params = route.params && typeof route.params === "object" ? route.params : {};
	const detail = params.detail != null ? params.detail : null;
	const boPackingId = params.bo_packing_id != null ? params.bo_packing_id : null;
	// Always consume, even when params already decide it — no stale flag left behind.
	const flagged = consumeRefresh(route.fullPath || route.path);
	return { refresh: flagged || detail != null || boPackingId != null, detail, boPackingId };
}
